import type { DocWritingFormat } from '../api/docWritingStream'
import { isDocumentBodyEmpty } from './documentBodyEmpty'

export type WelcomeSuggestion = { label: string; prompt: string }

export function buildWelcomeGreeting(
  title: string,
  category: string,
  format: DocWritingFormat,
  getDocumentContent: () => string,
  inline = false,
): string {
  const docTitle = title.trim()
  const docCategory = category.trim()
  const subject = docTitle || docCategory
  const hasBody = !isDocumentBodyEmpty(getDocumentContent(), format)
  const insertHint = inline ? '写完后可插入当前光标位置。' : '写完后可插入编辑器。'

  if (subject && !hasBody) {
    return inline
      ? `我可以在这里帮你写「${subject}」相关内容。当前光标之前还没有正文，可以从零起草。${insertHint}\n\n选一个方向，或直接告诉我写什么。`
      : `我可以帮你写「${subject}」相关的正文。\n\n你可以让我直接起草完整内容，比如工作报告、宣传文案、项目案例或方案说明；${insertHint}\n\n先选一个方向，或直接告诉我写什么、给谁看、要多少字。`
  }
  if (subject && hasBody) {
    return inline
      ? `我会根据光标前后的正文继续写，生成内容将插入当前光标处。${insertHint}\n\n选一项，或直接说你的需求。`
      : `我可以在这里继续写「${subject}」——续写、改语气、补案例或写摘要都可以。${insertHint}\n\n选一项，或直接说你的需求。`
  }
  if (!hasBody) {
    return inline
      ? `我是嵌入式写作助手。告诉我你要写什么（报告、文案、案例、方案等），${insertHint}\n\n先选常见类型，或直接描述需求。`
      : `我是文档写作助手。告诉我你要写什么（报告、文案、案例、方案等），${insertHint}\n\n先选常见类型，或直接描述需求。`
  }
  return inline
    ? `我会基于光标前后的正文继续写——续写、润色或提炼摘要。${insertHint}\n\n选一项，或直接输入。`
    : `我可以基于当前正文继续写——续写、润色或提炼摘要。${insertHint}\n\n选一项，或直接输入。`
}

export function buildWelcomeSuggestions(
  title: string,
  category: string,
  format: DocWritingFormat,
  getDocumentContent: () => string,
  inline = false,
): WelcomeSuggestion[] {
  const docTitle = title.trim()
  const docCategory = category.trim()
  const topic = docTitle || docCategory
  const hasBody = !isDocumentBodyEmpty(getDocumentContent(), format)

  if (!hasBody) {
    if (topic) {
      return [
        {
          label: `写一份「${topic}」工作报告`,
          prompt: `请围绕「${topic}」写一份完整的工作报告，包含背景、主要工作、成果数据、问题与下一步计划，语气正式、可直接用于汇报。`,
        },
        {
          label: `写「${topic}」宣传文案`,
          prompt: `请为「${topic}」写一段对外宣传文案，突出价值与亮点，语言简洁有说服力，适合官网或宣传册使用。`,
        },
        {
          label: `写「${topic}」项目案例`,
          prompt: `请围绕「${topic}」写一个项目成功案例，包含客户背景、痛点、解决方案、实施过程与效果，结构清晰、偏商务风格。`,
        },
        {
          label: `写「${topic}」方案说明`,
          prompt: `请为「${topic}」撰写一份方案说明，包含目标、实施思路、关键步骤与预期收益，便于内部评审或对外提案。`,
        },
      ]
    }
    return [
      {
        label: '写一份工作报告',
        prompt:
          '请写一份完整的工作报告，包含本期工作概述、重点成果、存在问题与下阶段计划，语气正式，可直接用于周报或月报。',
      },
      {
        label: '写产品介绍文案',
        prompt: '请写一段产品介绍文案，说明产品是什么、解决什么问题、核心优势与适用场景，语言简洁、偏市场宣传风格。',
      },
      {
        label: '写项目成功案例',
        prompt: '请写一个项目成功案例，包含客户背景、面临挑战、解决方案、实施亮点与最终效果，适合售前或官网展示。',
      },
      {
        label: '写技术/实施方案',
        prompt: '请写一份实施方案说明，包含背景目标、总体思路、实施步骤、资源安排与预期效果，结构清晰、便于评审。',
      },
    ]
  }

  const subject = topic || '这篇文档'
  const continuePrompt = inline
    ? '请结合光标前后正文，在光标处继续写下去，保持语气与结构一致；不要重复光标之后已有内容，仅做必要补充或过渡。'
    : '请根据现有正文继续写下去，保持语气、结构与详略一致，补充必要细节，不要重复已有内容。'
  return [
    {
      label: '续写完善正文',
      prompt: continuePrompt,
    },
    {
      label: '改写成汇报口径',
      prompt: '请将当前正文改写成适合向上汇报的正式表达，保留关键信息，条理更清晰，语气更凝练专业。',
    },
    {
      label: '提炼执行摘要',
      prompt: `请根据正文为「${subject}」写一段 200 字以内的执行摘要，突出结论、关键数据与建议行动。`,
    },
    {
      label: '补充案例与细节',
      prompt: '请通读正文，在合适位置补充具体案例、数据或实施细节，使内容更有说服力，并给出完整润色后的版本。',
    },
  ]
}
